import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { fmtRelative } from "@/lib/format";
import { useTheme } from "@/lib/theme";

export default function AdminEmail() {
  const { branding } = useTheme();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    enabled: false, provider: "resend",
    sender_email: "onboarding@resend.dev", sender_name: branding.platform_name || "Acquilix",
    api_key: "",
    // R58 · ZeptoMail
    resend_api_key: "",
    zeptomail_api_key: "",
    zeptomail_region: "us",
    smtp_host: "", smtp_port: 587,
    smtp_username: "", smtp_password: "", smtp_use_tls: true,
  });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [log, setLog] = useState([]);

  const load = async () => {
    const [c, l] = await Promise.all([
      api.get("/admin/email-provider"),
      api.get("/admin/email-log"),
    ]);
    setCfg(c.data); setLog(l.data || []);
    setForm((f) => ({
      ...f,
      enabled: c.data.enabled,
      provider: c.data.provider || "resend",
      sender_email: c.data.sender_email || f.sender_email,
      sender_name: c.data.sender_name || f.sender_name,
      smtp_host: c.data.smtp_host || f.smtp_host,
      smtp_port: c.data.smtp_port || f.smtp_port,
      smtp_use_tls: c.data.smtp_use_tls ?? f.smtp_use_tls,
      // R58
      zeptomail_region: c.data.zeptomail_region || f.zeptomail_region,
    }));
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        enabled: form.enabled,
        provider: form.provider,
        sender_email: form.sender_email,
        sender_name: form.sender_name,
        api_key: form.api_key || null,
        // R58 · ZeptoMail + explicit resend key alias
        resend_api_key: form.resend_api_key || null,
        zeptomail_api_key: form.zeptomail_api_key || null,
        zeptomail_region: form.zeptomail_region,
        smtp_host: form.smtp_host || null,
        smtp_port: form.smtp_port ? Number(form.smtp_port) : null,
        smtp_username: form.smtp_username || null,
        smtp_password: form.smtp_password || null,
        smtp_use_tls: form.smtp_use_tls,
      };
      await api.post("/admin/email-provider", payload);
      toast.success("Email provider saved");
      setForm({ ...form, api_key: "", resend_api_key: "", zeptomail_api_key: "", smtp_username: "", smtp_password: "" });
      load();
    } catch (e) {
      toast.error("Failed");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo) return toast.error("Enter a recipient");
    try {
      const { data } = await api.post("/admin/email-provider/test", { to: testTo });
      if (data.sent) toast.success(`Sent via ${data.provider}`);
      else toast.warning(data.reason || data.error || "Logged only (provider disabled)");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Communications"
        title="Email provider"
        description="Configure Resend or generic SMTP. When disabled, all transactional emails are still logged but not sent."
        testid="admin-email-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-8 max-w-4xl space-y-6">
        <div className="grid grid-cols-3 gap-3">
          <Stat label="Enabled" value={cfg?.enabled ? "Yes" : "No"} highlight={cfg?.enabled} testid="email-stat-enabled" />
          <Stat label="Provider" value={cfg?.provider || "—"} testid="email-stat-provider" />
          <Stat label="Configured" value={cfg?.configured ? "Yes" : "No"} highlight={cfg?.configured} testid="email-stat-configured" />
        </div>

        <form onSubmit={save} className="border border-border p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <Label className="label-eyebrow">Enable email sending</Label>
              <p className="text-xs text-muted-foreground">When off, emails are queued to db.email_log without being sent.</p>
            </div>
            <Switch checked={form.enabled} onCheckedChange={set("enabled")} data-testid="email-enable-switch" />
          </div>

          <div>
            <Label className="label-eyebrow mb-2 inline-block">Provider</Label>
            <RadioGroup value={form.provider} onValueChange={set("provider")} className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="resend" data-testid="email-provider-resend" />
                <span className="text-sm">Resend</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="zeptomail" data-testid="email-provider-zeptomail" />
                <span className="text-sm">ZeptoMail (Zoho)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="smtp" data-testid="email-provider-smtp" />
                <span className="text-sm">SMTP</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="log_only" data-testid="email-provider-log" />
                <span className="text-sm">Log only (no send)</span>
              </label>
            </RadioGroup>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Sender email" value={form.sender_email} onChange={set("sender_email")} testid="email-sender-input" />
            <Field label="Sender name" value={form.sender_name} onChange={set("sender_name")} testid="email-sender-name-input" />
          </div>

          {form.provider === "resend" && (
            <Field
              label="Resend API key"
              type="password"
              placeholder={cfg?.has_resend_key ? "•••• keep existing if blank" : "re_..."}
              value={form.resend_api_key || form.api_key}
              onChange={set("resend_api_key")}
              testid="email-resend-key-input"
            />
          )}

          {form.provider === "zeptomail" && (
            <div className="space-y-3 border-l-2 border-blue-500/60 pl-4 py-2">
              <Field
                label="ZeptoMail Send-Mail Token"
                type="password"
                placeholder={cfg?.has_zeptomail_key ? "•••• keep existing if blank" : "Zoho-enczapikey token"}
                value={form.zeptomail_api_key}
                onChange={set("zeptomail_api_key")}
                testid="email-zeptomail-key-input"
              />
              <div>
                <Label className="label-eyebrow mb-2 inline-block">Zoho region / data center</Label>
                <RadioGroup value={form.zeptomail_region} onValueChange={set("zeptomail_region")} className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="us" data-testid="email-zepto-region-us" />
                    <span className="text-sm">US (.com)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="eu" data-testid="email-zepto-region-eu" />
                    <span className="text-sm">EU (.eu)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="in" data-testid="email-zepto-region-in" />
                    <span className="text-sm">IN (.in)</span>
                  </label>
                </RadioGroup>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Get your Send-Mail Token from ZeptoMail dashboard → Mail Agents → your Agent → SMTP/API → API tab.
              </p>
            </div>
          )}

          {form.provider === "smtp" && (
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="SMTP host" value={form.smtp_host} onChange={set("smtp_host")} placeholder="smtp.example.com" testid="email-smtp-host-input" />
              <Field label="SMTP port" type="number" value={form.smtp_port} onChange={set("smtp_port")} testid="email-smtp-port-input" />
              <Field label="SMTP username" value={form.smtp_username} onChange={set("smtp_username")} placeholder="•••• keep existing if blank" testid="email-smtp-user-input" />
              <Field label="SMTP password" type="password" value={form.smtp_password} onChange={set("smtp_password")} placeholder="•••• keep existing if blank" testid="email-smtp-pass-input" />
              <div className="flex items-center gap-3 md:col-span-2">
                <Switch checked={!!form.smtp_use_tls} onCheckedChange={set("smtp_use_tls")} data-testid="email-smtp-tls-switch" />
                <span className="text-sm">Use STARTTLS</span>
              </div>
            </div>
          )}

          <Button type="submit" disabled={busy} className="rounded-none" data-testid="email-save-btn">
            <Mail className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save configuration"}
          </Button>
        </form>

        <div className="border border-border p-6 space-y-3">
          <div className="label-eyebrow">Send test email</div>
          <div className="flex gap-2">
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="h-10" data-testid="email-test-to-input" />
            <Button onClick={sendTest} className="rounded-none" data-testid="email-test-send-btn"><Send className="w-4 h-4 mr-2" /> Send test</Button>
          </div>
        </div>

        <div className="border border-border">
          <div className="px-4 py-3 border-b border-border label-eyebrow text-[10px] bg-muted/40">Email log (most recent)</div>
          {log.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground">No emails yet.</div>}
          {log.slice(0, 50).map((e) => (
            <div key={e.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`email-log-${e.id}`}>
              <div className="col-span-4 truncate"><span className="font-medium">{e.to}</span></div>
              <div className="col-span-4 truncate">{e.subject}</div>
              <div className="col-span-1 text-xs text-muted-foreground">{e.provider || "—"}</div>
              <div className="col-span-2">
                <span className={
                  e.status === "sent" ? "chip chip-approved" :
                  e.status === "failed" ? "chip chip-changes" :
                  e.status === "logged_only" ? "chip chip-pending" : "chip chip-open"
                }>{e.status}</span>
              </div>
              <div className="col-span-1 text-right text-xs text-muted-foreground">{fmtRelative(e.created_at)}</div>
            </div>
          ))}
        </div>

        <InboundOrphansSection />
      </div>
    </>
  );
}

function InboundOrphansSection() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setBusy(true); setError(null);
    try {
      const { data } = await api.get("/admin/inbound-orphans", { params: { limit: 50 } });
      setRows(data.orphans || []); setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load orphan payloads");
    }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this orphan payload permanently?")) return;
    try {
      await api.delete(`/admin/inbound-orphans/${id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };
  const view = async (id) => {
    try {
      const { data } = await api.get(`/admin/inbound-orphans/${id}`);
      setViewing(data);
    } catch { toast.error("Load failed"); }
  };

  return (
    <div className="border border-border" data-testid="inbound-orphans">
      <div className="px-4 py-3 border-b border-border label-eyebrow text-[10px] bg-muted/40 flex items-center justify-between">
        <span>Unrouted inbound emails ({total})</span>
        <Button size="sm" variant="ghost" className="rounded-none h-7" onClick={load}>Refresh</Button>
      </div>
      {busy ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">Loading…</div>
      ) : error ? (
        <div className="px-4 py-6 text-sm border-l-2 border-red-500 bg-red-50 text-red-800" data-testid="orphans-error">
          Couldn&apos;t load orphan payloads: {error}
        </div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          Nothing here — every inbound Resend webhook was routed to a workspace. 🎉
        </div>
      ) : (
        rows.map((r) => (
          <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm" data-testid={`orphan-${r.id}`}>
            <div className="col-span-3 truncate text-xs font-mono text-muted-foreground">{r.from}</div>
            <div className="col-span-3 truncate text-xs font-mono text-muted-foreground">→ {r.to}</div>
            <div className="col-span-3 truncate">{r.subject}</div>
            <div className="col-span-2 text-right text-xs text-muted-foreground">{fmtRelative(r.received_at)}</div>
            <div className="col-span-1 text-right">
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-1" onClick={() => view(r.id)}>View</Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 px-1 text-red-600" onClick={() => remove(r.id)}>×</Button>
            </div>
          </div>
        ))
      )}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setViewing(null)}>
          <div className="bg-background border border-border max-w-3xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="label-eyebrow">Raw inbound payload</div>
              <Button size="sm" variant="ghost" className="rounded-none" onClick={() => setViewing(null)}>Close</Button>
            </div>
            <pre className="text-xs font-mono whitespace-pre-wrap break-all bg-muted p-4">{JSON.stringify(viewing.payload || viewing, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, highlight, testid }) {
  return (
    <div className={`border p-5 ${highlight ? "bg-foreground text-background border-foreground" : "border-border"}`} data-testid={testid}>
      <div className="label-eyebrow opacity-70">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <Label className="label-eyebrow mb-2 inline-block">{label}</Label>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10" data-testid={testid} />
    </div>
  );
}
